import React, { useRef } from 'react';
import { Mail, MapPin, Globe } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { TowerIcon } from './TowerIcon';

const quickLinks = [
  { label: 'Home', to: '/#home', target: 'home' },
  { label: 'Our Services', to: '/#services', target: 'services' },
  { label: 'Why Choose Us', to: '/#why-choose-us', target: 'why-choose-us' },
  { label: 'Industries We Support', to: '/#industries', target: 'industries' },
  { label: 'About Us', to: '/#about-us', target: 'about-us' },
  { label: 'How We Work', to: '/#how-we-work', target: 'how-we-work' },
  { label: 'Careers', to: '/careers', target: 'careers', page: true },
  { label: 'Contact', to: '/#contact', target: 'contact' },
];

const serviceLinks = [
  { label: 'Site Supervision', to: '/#site-supervision', target: 'site-supervision' },
  { label: 'Mechanical Installation Support', to: '/#mechanical-installation-support', target: 'mechanical-installation-support' },
  { label: 'Electrical Installation Support', to: '/#electrical-installation-support', target: 'electrical-installation-support' },
  { label: 'Automation & PLC Support', to: '/#automation-plc-support', target: 'automation-plc-support' },
  { label: 'Commissioning & Start-up', to: '/#commissioning-startup', target: 'commissioning-startup' },
  { label: 'Project Coordination', to: '/#project-coordination', target: 'project-coordination' },
  { label: 'Technical Personnel', to: '/#technical-personnel', target: 'technical-personnel' },
  { label: 'Troubleshooting & Technical Support', to: '/#troubleshooting-support', target: 'troubleshooting-support' },
];

export const Footer = () => {
  const location = useLocation();
  const clickTimestamps = useRef([]);

  const handleAnchorClick = (e, targetId) => {
    if (targetId === null) return; // page-type link — let router navigate
    // On homepage, prevent full nav and smooth-scroll
    if (location.pathname === '/' && targetId) {
      e.preventDefault();
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', `/#${targetId}`);
      }
    }
    // On legal pages, Link default routes to /#target; ScrollToHash in App.js scrolls
  };

  const handleCopyrightBrandActivate = () => {
    // Detect three activations within ~2 seconds on the copyright "Tower
    // Technology" words. Works for both mouse click and touch tap because
    // touchend on non-interactive elements also fires a synthetic click.
    const now = Date.now();
    clickTimestamps.current = [
      ...clickTimestamps.current.filter((t) => now - t < 2000),
      now,
    ];
    if (clickTimestamps.current.length >= 3) {
      clickTimestamps.current = [];
      window.dispatchEvent(new CustomEvent('tt:open-admin-login'));
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-10">
          {/* Company */}
          <div className="lg:col-span-4">
            <Link
              to="/"
              onClick={(e) => handleAnchorClick(e, 'home')}
              className="flex items-center space-x-3 mb-4 hover:opacity-90 transition-opacity"
              aria-label="Tower Technology home"
              data-testid="footer-brand"
            >
              <TowerIcon size={36} className="opacity-90" />
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                Tower Technology
              </h3>
            </Link>
            <p className="text-gray-400 leading-relaxed text-sm md:text-base max-w-md">
              Engineering and technical support for industrial projects across China.
            </p>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-bold mb-4 text-sm tracking-wider uppercase">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.target}>
                  <Link
                    to={link.to}
                    onClick={(e) => handleAnchorClick(e, link.page ? null : link.target)}
                    data-testid={`footer-link-${link.target}`}
                    className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-bold mb-4 text-sm tracking-wider uppercase">
              Services
            </h4>
            <ul className="space-y-2.5">
              {serviceLinks.map((service) => (
                <li key={service.target}>
                  <Link
                    to={service.to}
                    onClick={(e) => handleAnchorClick(e, service.target)}
                    data-testid={`footer-service-${service.target}`}
                    className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200"
                  >
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-bold mb-4 text-sm tracking-wider uppercase">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400">Xiamen (Fujian), China</span>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:office@towertech-eng.com"
                  data-testid="footer-email"
                  className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200 break-all"
                >
                  office@towertech-eng.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-400 leading-relaxed">
                  <div>Website:</div>
                  <a
                    href="https://towertech-eng.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-website"
                    className="hover:text-blue-400 transition-colors duration-200"
                  >
                    towertech-eng.com
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="text-sm text-gray-400">
              <p>
                © 2026{' '}
                <span
                  onClick={handleCopyrightBrandActivate}
                  data-testid="copyright-brand"
                  className="cursor-default select-none"
                >
                  Tower Technology
                </span>
                . All rights reserved.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Industrial Engineering Support in China
              </p>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link
                to="/privacy-policy"
                data-testid="footer-privacy-policy"
                className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                data-testid="footer-terms-of-service"
                className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookie-policy"
                data-testid="footer-cookie-policy"
                className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
